/**
 * Words and numbers for the AI usage page (MYD-630). Pure, so tested alone.
 */

export type AiUsageRangeKey = '7d' | '30d' | '90d' | 'month'

export const RANGE_OPTIONS: { key: AiUsageRangeKey; label: string }[] = [
	{ key: '7d', label: 'Last 7 days' },
	{ key: '30d', label: 'Last 30 days' },
	{ key: '90d', label: 'Last 90 days' },
	{ key: 'month', label: 'This month' },
]

const DAY_MS = 24 * 60 * 60 * 1000
const ymd = (date: Date) => date.toISOString().slice(0, 10)

/** The range as UTC days, which is how the server, and the limits, count. */
export const rangeFor = (key: AiUsageRangeKey, now: Date = new Date()): { from: string; to: string } => {
	const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
	if (key === 'month') return { from: ymd(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))), to: ymd(today) }
	const days = key === '7d' ? 7 : key === '90d' ? 90 : 30
	return { from: ymd(new Date(today.getTime() - (days - 1) * DAY_MS)), to: ymd(today) }
}

/**
 * Dollars. AI calls cost fractions of a cent, so small amounts keep their
 * digits ("$0.0042") where a ledger rounding to cents would show "$0.00".
 */
export const formatUsd = (value: number | null | undefined): string => {
	if (value == null) return '—'
	const digits = value !== 0 && Math.abs(value) < 1 ? 4 : 2
	return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: digits }).format(value)
}

export const formatCount = (value: number | null | undefined): string => (value == null ? '—' : new Intl.NumberFormat('en-US').format(value))

/** "1.2M", "34.5k": tokens run to millions. */
export const formatTokens = (value: number | null | undefined): string => {
	if (value == null) return '—'
	if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
	if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`
	return String(value)
}

export const formatPercent = (ratio: number | null | undefined): string => (ratio == null ? '—' : `${(ratio * 100).toFixed(ratio < 0.1 && ratio > 0 ? 1 : 0)}%`)

export const formatMs = (ms: number | null | undefined): string => {
	if (ms == null) return '—'
	return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`
}

/** How full the monthly cap is: calm, close, or over. */
export const capTone = (share: number | null | undefined): 'ok' | 'warn' | 'over' => {
	if (share == null) return 'ok'
	if (share >= 1) return 'over'
	if (share >= 0.8) return 'warn'
	return 'ok'
}

export const OUTCOME_LABELS: Record<string, string> = {
	answer: 'Answered',
	plan: 'Proposed a plan',
	refusal: 'Refused',
	error: 'Failed',
	budget: 'Over a limit',
}
